import { MM_TO_PT, type Line, type Paragraph, type ParagraphStyle, type Snapshot, type Story, type TextFrameItem } from '@proofroom/schema';

/**
 * Two spreads, a two-column story, a rect and a placeholder image. Line geometry is
 * approximated from Helvetica AFM widths, so fidelity logs will show textLength adjustments
 * here that a real InDesign export does not.
 */

// Approximate Helvetica advance widths (1/1000 em), used only to lay out this fixture's plausible lines.
const AFM_WIDTHS: Record<string, number> = {
  ' ': 278, '!': 278, '"': 355, '#': 556, '$': 556, '%': 889, '&': 667, "'": 191, '(': 333, ')': 333, '*': 389, '+': 584,
  ',': 278, '-': 333, '.': 278, '/': 278, '0': 556, '1': 556, '2': 556, '3': 556, '4': 556, '5': 556, '6': 556, '7': 556,
  '8': 556, '9': 556, ':': 278, ';': 278, '<': 584, '=': 584, '>': 584, '?': 556, '@': 1015,
  A: 667, B: 667, C: 722, D: 722, E: 667, F: 611, G: 778, H: 722, I: 278, J: 500, K: 667, L: 556, M: 833, N: 722, O: 778,
  P: 667, Q: 778, R: 722, S: 667, T: 611, U: 722, V: 667, W: 944, X: 667, Y: 667, Z: 611,
  a: 556, b: 556, c: 500, d: 556, e: 556, f: 278, g: 556, h: 556, i: 222, j: 222, k: 500, l: 222, m: 833, n: 556, o: 556,
  p: 556, q: 556, r: 333, s: 500, t: 278, u: 556, v: 500, w: 722, x: 500, y: 500, z: 500,
  '’': 222, '‘': 222, '“': 333, '”': 333, '–': 556, '\u2014': 1000, '\u00A0': 278,
};

function afmWidth(text: string, pointSize: number, tracking = 0, bold = false): number {
  const track = (tracking / 1000) * pointSize;
  return [...text].reduce((sum, ch) => {
    const base = AFM_WIDTHS[ch] ?? 556;
    return sum + ((bold ? base * 1.06 : base) / 1000) * pointSize + track;
  }, 0);
}

const A4 = { w: 210 * MM_TO_PT, h: 297 * MM_TO_PT };
const margin = 15 * MM_TO_PT;
const black = { swatch: 'Black', tint: 100, hex: '#141414' };
const blue = { swatch: 'NGED Blue', tint: 100, hex: '#1d4f91' };

const base: ParagraphStyle['attrs'] = {
  fontId: 'HelveticaNeue-Light', pointSize: 9.5, leading: 13, autoLeading: 120, tracking: 0, kerning: 'metrics', fill: black,
  ligatures: true, capitalization: 'normal', baselineShift: 0, horizontalScale: 100, verticalScale: 100, noBreak: false,
  underline: false, strikethrough: false, language: 'English: UK', align: 'left', composer: 'paragraph',
  hyphenation: { enabled: false, wordsLongerThan: 5, afterFirst: 2, beforeLast: 2, ladderLimit: 3, zone: 36, lastWord: false, capitalized: true, acrossColumns: true, weight: 5 },
  wordSpacing: { min: 80, desired: 100, max: 133 }, letterSpacing: { min: 0, desired: 0, max: 0 }, glyphScaling: { min: 100, desired: 100, max: 100 },
  singleWordJustification: 'left', leftIndent: 0, rightIndent: 0, firstLineIndent: 0, spaceBefore: 0, spaceAfter: 6,
  alignToBaselineGrid: false, keepLinesTogether: false, keepFirstLines: 2, keepLastLines: 2, keepWithNext: 0,
  span: { type: 'none', count: 1, insideGutter: 0, outsideGutter: 0 }, tabs: [],
};

const styles: ParagraphStyle[] = [
  { id: 'ps-body', name: 'Body', attrs: { ...base } },
  { id: 'ps-heading', name: 'Page Heading', attrs: { ...base, fontId: 'HelveticaNeue-Bold', pointSize: 26, leading: 30, tracking: -10, spaceAfter: 12, fill: blue, hyphenation: { ...base.hyphenation, enabled: true } } },
  { id: 'ps-sub', name: 'Page Sub Heading', attrs: { ...base, fontId: 'HelveticaNeue-Medium', pointSize: 12, leading: 15, spaceBefore: 8, spaceAfter: 4, fill: blue } },
  { id: 'ps-quote', name: 'Pull Quote', attrs: { ...base, fontId: 'HelveticaNeue-Light', pointSize: 16, leading: 21, tracking: -5, fill: blue } },
  { id: 'ps-caption', name: 'Caption', attrs: { ...base, pointSize: 7.5, leading: 10, fill: { swatch: 'Black', tint: 70, hex: '#5b5b5b' } } },
  { id: 'ps-cover', name: 'Cover Title', attrs: { ...base, fontId: 'HelveticaNeue-Bold', pointSize: 44, leading: 46, tracking: -20, fill: { swatch: 'Paper', tint: 100, hex: '#ffffff' } } },
];

const styleById = Object.fromEntries(styles.map((s) => [s.id, s]));

function para(id: string, styleId: string, text: string, runs?: Paragraph['runs']): Paragraph {
  return { id, styleId, text, runs: runs ?? [{ start: 0, end: text.length }] };
}

const storyMain: Story = {
  id: 'story-1001',
  frameIds: ['tf-1001'],
  paragraphs: [
    para('p-1', 'ps-heading', 'Keeping the lights on across the Midlands'),
    para('p-2', 'ps-sub', 'What the network is for'),
    para('p-3', 'ps-body', 'National Grid Electricity Distribution owns and operates the wires that carry power from the transmission system to eight million homes and businesses. Every substation, cable and pole in this document is part of that network, and every one of them has a maintenance story behind it.'),
    para('p-4', 'ps-body', 'Our engineers work through the night when storms bring down lines, and through the summer when demand from air conditioning and electric vehicles peaks. This edition explains how we plan that work, how we pay for it, and what it means for your bill.', [
      { start: 0, end: 13 }, { start: 13, end: 63, overrides: { fontId: 'HelveticaNeue-Medium' } }, { start: 63, end: 253 },
    ]),
    para('p-5', 'ps-sub', 'Investment in the next five years'),
    para('p-6', 'ps-body', 'Between now and 2028 we will invest more than six billion pounds in reinforcing the network, replacing ageing assets and connecting new sources of low-carbon generation. Around a third of that money goes on underground cables in towns and cities, where a fault can leave thousands of customers without supply.'),
    para('p-7', 'ps-body', 'The rest is spent on overhead lines, on the substations that step voltage down for local use, and on the digital systems that let us see and control the network in real time. You can read the full plan on our website, or ask your local community engineer for a printed copy.'),
  ],
};

const storyQuote: Story = {
  id: 'story-1002', frameIds: ['tf-1002'],
  paragraphs: [para('p-q1', 'ps-quote', '“We do not get to choose the weather. We do get to choose how ready we are for it.”')],
};
const storyCaption: Story = {
  id: 'story-1003', frameIds: ['tf-1003'],
  paragraphs: [para('p-c1', 'ps-caption', 'Placeholder: a line crew at work near Derby. Final image to be supplied by the client.')],
};
const storyCover: Story = {
  id: 'story-1000', frameIds: ['tf-1000'],
  paragraphs: [para('p-cv1', 'ps-cover', 'Network News'), para('p-cv2', 'ps-quote', 'Edition three, autumn 2026')],
};

/** Greedy first-fit lines from AFM widths, so the mock has plausible InDesign-like geometry. */
function layout(story: Story, frame: Omit<TextFrameItem, 'lines' | 'overset'>): { lines: Line[]; overset: boolean } {
  const [t, l, b, r] = frame.insets;
  const n = frame.columns.count, g = frame.columns.gutter;
  const innerW = frame.bounds.w - l - r, innerH = frame.bounds.h - t - b;
  const colW = (innerW - g * (n - 1)) / n;
  const top = frame.bounds.y + t, bottom = top + innerH;
  const lines: Line[] = [];
  let col = 0, baseline = 0, started = false;
  let overset = false;
  for (const p of story.paragraphs) {
    const st = styleById[p.styleId].attrs;
    const leading = st.leading as number;
    const size = st.pointSize;
    const bold = /bold|medium/i.test(st.fontId);
    const ascent = size * 0.952, descent = size * 0.213;
    const words = p.text.split(' ');
    let i = 0, first = true;
    while (i < words.length) {
      let j = i, text = '';
      while (j < words.length) {
        const next = text ? text + ' ' + words[j] : words[j];
        if (afmWidth(next, size, st.tracking, bold) > colW && text) break;
        text = next; j++;
      }
      const lineStart = words.slice(0, i).join(' ').length + (i > 0 ? 1 : 0);
      const lineEnd = lineStart + text.length + (j < words.length ? 1 : 0);
      if (!started) { baseline = top + ascent; started = true; } else baseline += leading + (first ? st.spaceBefore : 0);
      if (baseline > bottom) {
        if (col < n - 1) { col++; baseline = top + ascent; } else { overset = true; break; }
      }
      lines.push({ paraId: p.id, start: lineStart, end: lineEnd, frameId: frame.id, column: col, x: frame.bounds.x + l + col * (colW + g), baseline, width: afmWidth(text, size, st.tracking, bold), ascent, descent });
      i = j; first = false;
    }
    if (overset) break;
    baseline += st.spaceAfter;
  }
  return { lines, overset };
}

function frame(id: string, storyId: string, bounds: TextFrameItem['bounds'], columns: number, layerId: string, pageId: string, z: number): TextFrameItem {
  const f: Omit<TextFrameItem, 'lines' | 'overset'> = {
    id, kind: 'text', name: `frame ${id}`, layerId, pageId, bounds, rotation: 0, z, opacity: 100, visible: true,
    storyId, threadIndex: 0, columns: { count: columns, gutter: 5 * MM_TO_PT }, insets: [0, 0, 0, 0], verticalAlign: 'top',
    firstBaseline: 'ascent', firstBaselineMin: 0,
  };
  const story = [storyMain, storyQuote, storyCaption, storyCover].find((s) => s.id === storyId)!;
  const { lines, overset } = layout(story, f);
  return { ...f, lines, overset };
}

const pageW = A4.w, pageH = A4.h;

export const miniSnapshot: Snapshot = {
  schemaVersion: 1,
  documentId: 'doc-mini',
  documentName: 'ED3 mini fixture',
  exportedAt: '2026-08-27T09:00:00.000Z',
  exportedBy: 'portal-mini-fixture',
  units: 'pt',
  pageSize: A4,
  bleed: 3 * MM_TO_PT,
  facingPages: true,
  baselineGrid: { start: 0, increment: 13 },
  fonts: [
    { id: 'HelveticaNeue-Light', family: 'Helvetica Neue', style: 'Light', postscriptName: 'HelveticaNeue-Light' },
    { id: 'HelveticaNeue-Bold', family: 'Helvetica Neue', style: 'Bold', postscriptName: 'HelveticaNeue-Bold' },
    { id: 'HelveticaNeue-Medium', family: 'Helvetica Neue', style: 'Medium', postscriptName: 'HelveticaNeue-Medium' },
    { id: 'HelveticaNeue-Italic', family: 'Helvetica Neue', style: 'Italic', postscriptName: 'HelveticaNeue-Italic' },
    { id: 'HelveticaNeue-LightItalic', family: 'Helvetica Neue', style: 'Light Italic', postscriptName: 'HelveticaNeue-LightItalic' },
    { id: 'HelveticaNeue-BoldItalic', family: 'Helvetica Neue', style: 'Bold Italic', postscriptName: 'HelveticaNeue-BoldItalic' },
  ],
  swatches: [
    { name: 'Black', space: 'CMYK', values: [0, 0, 0, 100], hex: '#141414' },
    { name: 'Paper', space: 'CMYK', values: [0, 0, 0, 0], hex: '#ffffff' },
    { name: 'NGED Blue', space: 'CMYK', values: [100, 70, 0, 20], hex: '#1d4f91' },
    { name: 'NGED Yellow', space: 'CMYK', values: [0, 15, 100, 0], hex: '#f5c400' },
    { name: 'NGED Green', space: 'CMYK', values: [70, 0, 100, 0], hex: '#4caf50' },
  ],
  paragraphStyles: styles,
  characterStyles: [{ id: 'cs-emph', name: 'Emphasis', attrs: { fontId: 'HelveticaNeue-Medium' } }],
  layers: [
    { id: 'layer-art', name: 'Artwork', visible: true, locked: false, index: 0 },
    { id: 'layer-text', name: 'Text', visible: true, locked: false, index: 1 },
    { id: 'layer-optA', name: 'Creative option A', visible: true, locked: false, index: 2 },
    { id: 'layer-optB', name: 'Creative option B', visible: false, locked: false, index: 3 },
  ],
  spreads: [
    {
      id: 'spread-1', index: 0, bounds: { x: 0, y: 0, w: pageW, h: pageH },
      pages: [{ id: 'page-1', index: 0, number: '1', bounds: { x: 0, y: 0, w: pageW, h: pageH }, side: 'right', margins: { top: margin, left: margin, bottom: margin, right: margin }, columns: { count: 12, gutter: 4 * MM_TO_PT } }],
      items: [
        { id: 'rect-cover', kind: 'rect', name: 'cover block', layerId: 'layer-art', pageId: 'page-1', bounds: { x: 0, y: 0, w: pageW, h: pageH }, rotation: 0, z: 0, fill: blue, opacity: 100, visible: true },
        { id: 'rect-cover-band', kind: 'rect', name: 'band', layerId: 'layer-art', pageId: 'page-1', bounds: { x: 0, y: pageH * 0.62, w: pageW, h: 18 }, rotation: 0, z: 1, fill: { swatch: 'NGED Yellow', tint: 100, hex: '#f5c400' }, opacity: 100, visible: true },
        frame('tf-1000', 'story-1000', { x: margin, y: pageH * 0.4, w: pageW - margin * 2, h: 130 }, 1, 'layer-text', 'page-1', 2),
      ],
    },
    {
      id: 'spread-2', index: 1, bounds: { x: 0, y: 0, w: pageW * 2, h: pageH },
      pages: [
        { id: 'page-2', index: 1, number: '2', bounds: { x: 0, y: 0, w: pageW, h: pageH }, side: 'left', margins: { top: margin, left: margin, bottom: margin, right: margin }, columns: { count: 12, gutter: 4 * MM_TO_PT } },
        { id: 'page-3', index: 2, number: '3', bounds: { x: pageW, y: 0, w: pageW, h: pageH }, side: 'right', margins: { top: margin, left: margin, bottom: margin, right: margin }, columns: { count: 12, gutter: 4 * MM_TO_PT } },
      ],
      items: [
        { id: 'rect-tint', kind: 'rect', name: 'tint panel', layerId: 'layer-art', pageId: 'page-3', bounds: { x: pageW + margin, y: margin, w: pageW - margin * 2, h: 150 }, rotation: 0, z: 0, fill: { swatch: 'NGED Blue', tint: 15, hex: '#dde5f0' }, cornerRadius: 8, opacity: 100, visible: true },
        { id: 'rect-optA', kind: 'rect', name: 'option A accent', layerId: 'layer-optA', pageId: 'page-3', bounds: { x: pageW + margin, y: margin + 160, w: 120, h: 14 }, rotation: 0, z: 1, fill: { swatch: 'NGED Yellow', tint: 100, hex: '#f5c400' }, opacity: 100, visible: true },
        { id: 'rect-optB', kind: 'oval', name: 'option B accent', layerId: 'layer-optB', pageId: 'page-3', bounds: { x: pageW + margin, y: margin + 160, w: 120, h: 14 }, rotation: 0, z: 1, fill: { swatch: 'NGED Green', tint: 100, hex: '#4caf50' }, opacity: 100, visible: true },
        { id: 'img-placeholder', kind: 'image', name: 'hero placeholder', layerId: 'layer-art', pageId: 'page-3', bounds: { x: pageW + margin, y: margin + 190, w: pageW - margin * 2, h: 300 }, rotation: 0, z: 2, opacity: 100, visible: true, linkStatus: 'missing', graphicBounds: { x: pageW + margin, y: margin + 190, w: pageW - margin * 2, h: 300 }, isPlaceholder: true },
        frame('tf-1001', 'story-1001', { x: margin, y: margin, w: pageW - margin * 2, h: pageH - margin * 2 }, 2, 'layer-text', 'page-2', 3),
        frame('tf-1002', 'story-1002', { x: pageW + margin, y: margin + 20, w: pageW - margin * 2 - 30, h: 110 }, 1, 'layer-text', 'page-3', 4),
        frame('tf-1003', 'story-1003', { x: pageW + margin, y: margin + 496, w: pageW - margin * 2, h: 30 }, 1, 'layer-text', 'page-3', 5),
        {
          id: 'group-1', kind: 'group', name: 'footer group', layerId: 'layer-art', pageId: 'page-2', bounds: { x: margin, y: pageH - 30, w: 200, h: 12 }, rotation: 0, z: 6, opacity: 100, visible: true,
          children: [
            { id: 'rect-g1', kind: 'rect', layerId: 'layer-art', pageId: 'page-2', bounds: { x: margin, y: pageH - 30, w: 12, h: 12 }, rotation: 0, z: 0, fill: blue, opacity: 100, visible: true },
            { id: 'rect-g2', kind: 'rect', layerId: 'layer-art', pageId: 'page-2', bounds: { x: margin + 16, y: pageH - 30, w: 184, h: 12 }, rotation: 0, z: 1, fill: { swatch: 'NGED Yellow', tint: 100, hex: '#f5c400' }, opacity: 60, visible: true },
          ],
        },
      ],
    },
  ],
  stories: [storyCover, storyMain, storyQuote, storyCaption],
  assets: [],
};
